/* ============================================
   useLeadConversaciones — los chats de una oportunidad

   Resuelve las conversaciones a partir de los CONTACTOS
   del lead, nunca por teléfono: en producción hay
   placeholders como +51999999999 compartidos por varios
   leads, y matchear por número mostraría el hilo de otra
   persona.

   Vive acá y no dentro del panel de chat porque la ficha
   lo necesita en dos lugares: el panel que muestra el hilo
   y el envío de material desde la pestaña Material, que
   tiene que escribir en la MISMA conversación ya vinculada
   y no abrir una nueva.
   ============================================ */
import { useState, useEffect, useMemo } from 'react'
import { useApi } from '@/hooks/useApi'
import type { Lead, Conversacion } from '@/lib/types'

interface Resultado {
  /** Más reciente primero. */
  conversaciones: Conversacion[]
  loading: boolean
}

/**
 * @param soloContactoId Limita al hilo de un contacto (se entra desde su
 *   WhatsApp en la barra lateral). Sin esto junta los hilos de todo el lead.
 */
export function useLeadConversaciones(lead: Lead, soloContactoId: number | null = null): Resultado {
  const api = useApi()
  const [conversaciones, setConversaciones] = useState<Conversacion[]>([])
  const [loading, setLoading] = useState(true)

  /**
   * Ids de contacto, principal primero, como string. Va de clave del efecto
   * a propósito: un array nuevo en cada render lo re-dispararía en loop. Lo
   * mismo vale para `api`, que es un objeto literal nuevo por render — por eso
   * abajo se depende de `api.get`, que sí es estable (useCallback sin deps).
   */
  const contactoIdsKey = useMemo(() => {
    if (soloContactoId) return String(soloContactoId)
    const contactos = [...(lead.contactos || [])]
    contactos.sort((a, b) => Number(b.es_principal) - Number(a.es_principal))
    return contactos.map(c => c.contacto_id).join(',')
  }, [lead.contactos, soloContactoId])

  const apiGet = api.get

  useEffect(() => {
    let cancelado = false

    async function cargar() {
      setLoading(true)

      const contactoIds = contactoIdsKey ? contactoIdsKey.split(',').map(Number) : []
      if (contactoIds.length === 0) {
        if (!cancelado) { setConversaciones([]); setLoading(false) }
        return
      }

      // Un lead casi siempre tiene un contacto, pero la M:N permite más.
      // Si una consulta falla, las demás igual alcanzan para mostrar algo.
      const porContacto = await Promise.all(
        contactoIds.map(id =>
          apiGet<Conversacion[]>(`/api/chat/conversaciones?contacto_id=${id}`)
            .then(r => (Array.isArray(r) ? r : []))
            .catch(() => [] as Conversacion[])
        )
      )
      if (cancelado) return

      const unicas = new Map<number, Conversacion>()
      porContacto.flat().forEach(c => unicas.set(c.id, c))

      const ordenadas = [...unicas.values()].sort((a, b) => {
        const fa = a.ultimo_mensaje_at ? new Date(a.ultimo_mensaje_at).getTime() : 0
        const fb = b.ultimo_mensaje_at ? new Date(b.ultimo_mensaje_at).getTime() : 0
        return fb - fa
      })

      setConversaciones(ordenadas)
      setLoading(false)
    }

    cargar()
    return () => { cancelado = true }
  }, [contactoIdsKey, apiGet])

  return { conversaciones, loading }
}
